import React from 'react'
import { Image, ImageStyle, View } from 'react-native'
import moment from 'moment'
import { weightLossExperienceResultGraphImage } from '@src/assets/images'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useIsDarkMode } from '@src/config/theme'
import { Text } from '@src/components/base/Text'
import { SHORT_MONTH_AND_DATE_FORMAT } from '@src/config/momentFormat'
import { OnboardingExperience, UserPropertyOnboardingExperienceKind } from '@src/types'

const FOUR_WEEK_BACKGROUND_COLOR = '#B9E5F9'
const FOUR_WEEK_TEXT_COLOR = '#236582'
const GOAL_DATE_BACKGROUND_COLOR = '#4EBD60'
const GOAL_DATE_TEXT_COLOR = '#0A4013'

interface GraphProps {
  insurancePolicyIsEligible: boolean
  hidesGoalDate: boolean
  onboardingExperience?: OnboardingExperience | null
}

export const Graph = ({
  insurancePolicyIsEligible,
  hidesGoalDate,
  onboardingExperience,
}: GraphProps) => {
  const styles = useStyleSheet(themedStyles)
  const isDarkMode = useIsDarkMode()

  const isWeightLoss =
    onboardingExperience?.kind === UserPropertyOnboardingExperienceKind.WeightLoss
  const baselineWeight = onboardingExperience?.baselineWeight
  const fourWeekWeight = onboardingExperience?.fourWeekWeight
  const dateToReachDesiredWeight = onboardingExperience?.dateToReachDesiredWeight
  const desiredWeight = onboardingExperience?.desiredWeight

  return (
    <View style={{ position: 'relative' }}>
      <Image
        resizeMethod="resize"
        source={
          weightLossExperienceResultGraphImage(isDarkMode, insurancePolicyIsEligible, isWeightLoss)
            .imageSource
        }
        style={styles.image as ImageStyle}
      />

      {isWeightLoss && (
        <>
          <View style={styles.currentWeight}>
            <Text type="regular" style={styles.currentWeightText}>
              {baselineWeight}
            </Text>
          </View>

          {insurancePolicyIsEligible && (
            <View style={[styles.fourWeekWeight, { backgroundColor: FOUR_WEEK_BACKGROUND_COLOR }]}>
              <Text
                type="regular"
                style={[styles.fourWeekWeightText, { color: FOUR_WEEK_TEXT_COLOR }]}
              >
                {fourWeekWeight || 0} lb
              </Text>
            </View>
          )}

          <Text type="regular" style={[styles.goalDateText, { color: GOAL_DATE_TEXT_COLOR }]}>
            {hidesGoalDate
              ? 'On Track'
              : moment(dateToReachDesiredWeight).format(SHORT_MONTH_AND_DATE_FORMAT)}
          </Text>

          <View style={[styles.goalWeight, { backgroundColor: GOAL_DATE_BACKGROUND_COLOR }]}>
            <Text type="regular" style={styles.goalWeightText}>
              {desiredWeight || 0} lb
            </Text>
          </View>
        </>
      )}
    </View>
  )
}

const themedStyles = StyleService.create({
  image: {
    maxWidth: 330,
    height: undefined,
    maxHeight: 210,
    alignSelf: 'center',
    aspectRatio: 16 / 9,
    marginTop: 32,
  },
  currentWeight: {
    position: 'absolute',
    top: 66,
    left: 18,
    width: 45,
    borderRadius: 16,
  },
  currentWeightText: {
    color: 'theme.marketing.onText',
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 700,
  },
  fourWeekWeight: {
    position: 'absolute',
    top: 92,
    width: 60,
    left: 130,
  },
  fourWeekWeightText: {
    fontSize: 13,
    fontWeight: 700,
    textAlign: 'center',
  },
  goalDateText: {
    position: 'absolute',
    top: 117,
    right: 28,
    width: 60,
    fontSize: 12,
    fontWeight: 700,
    textAlign: 'center',
  },
  goalWeight: {
    position: 'absolute',
    bottom: 56,
    right: 15,
    width: 87,
  },
  goalWeightText: {
    color: 'theme.solid.white',
    fontSize: 13,
    fontWeight: 700,
    textAlign: 'center',
  },
})
